import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, sseQuerySchema } from '../schemas';
import { workerMergedTagSchema, managerMergedTagSchema } from '@shared/interfaces/IMergedTag';
import { mergedEquipmentSchema } from '@shared/interfaces/IMergedEquipment';
import { activeManagerSchema } from '@shared/interfaces/IActiveManager';
import { professionWithActiveTagsSchema } from '@shared/interfaces/IProfessionWithActiveTags';
import { resourceCountByLocationSchema } from '@shared/interfaces/IResourceCountByLocation';
import { floorViewSchema } from '@shared/interfaces/IFloorView';
import { fullMergedFloorViewDataSchema } from '@shared/interfaces/IFullMergedFloorsData';

// ============================================================
// Query Schemas
// ============================================================

const workersMergedTagsQuerySchema = z.object({
	areaId: z.string().optional(),
	floorId: z.string().optional(),
	professionsIds: z.array(z.string()).optional(),
});

const locationQuerySchema = z.object({
	floorId: z.string().optional(),
	areaId: z.string().optional(),
});

const deleteFloorsQuerySchema = z.object({
	floorIds: requiredStringSchema,
});

const resourceCountQuerySchema = z.object({
	professionIds: z.array(z.string()).optional(),
	floorIds: z.array(z.string()).optional(),
});

const equipmentCountQuerySchema = z.object({
	floorIds: z.array(z.string()).optional(),
});

// ============================================================
// Param Schemas
// ============================================================

const projectTagParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
	tagId: requiredStringSchema.describe('Tag identifier'),
});

// ============================================================
// Body Schemas
// ============================================================

/**
 * Was `{ mergedTag: <a whole merged tag> }`, of which the handler read exactly
 * three fields: `projectId`, `tagId` and `closestAnchor.floorId`. The first two
 * are the path now, and the third is all that is left — so the body says what
 * the route actually needs, and a caller can no longer hand over a tag whose
 * `projectId` disagrees with the one the guard validated.
 */
const tagLocationUpdateBodySchema = z.object({
	floorId: requiredStringSchema.describe('The floor the tag is now closest to'),
});

// ============================================================
// Contract
// ============================================================

/**
 * `{ root: true }` — so EVERY path in this file is spelled in full, including
 * the ones that are NOT migrating in this commit.
 *
 * This is the third time the trap has been set on a stream. Dropping the
 * contract-name prefix moves every route in the file, not only the ones being
 * changed: `/merged/floors/stream` would silently become `/floors/stream`, no
 * compiler would notice, and no test would fail unless it asserts an absolute
 * URL. An SSE URL is also a pub/sub channel key, matched by SUBSTRING against
 * the publisher strings in `locationRealtime.service.ts`, so the failure mode
 * is a stream that connects, sends its snapshot and then goes silent forever —
 * in the frontend and in d-orca, with no error in either.
 *
 * The four SSE routes and the three mutations below therefore spell `/merged/…`
 * explicitly, and their URLs are byte-identical to what they were before this
 * commit. The parsed route table was diffed against HEAD to prove it, because
 * the source diff cannot.
 */
export const mergedDataContract = defineContract(
	'merged',
	{
		getWorkersMergedTags: route({
			method: 'GET',
			path: '/projects/:projectId/workers',
			params: projectIdParamsSchema,
			query: workersMergedTagsQuerySchema,
			// Was the three-way `fullMergedTagSchema` union while the service returns
			// `IWorkerMergedTag[]`. The response schema drives serialization, so a union
			// that admits shapes the route never produces is both a lie to the caller
			// and a serializer that can accept the wrong branch.
			response: z.array(workerMergedTagSchema),
			operationId: 'getWorkersMergedTags',
			tags: ['Merged Data'],
			summary: 'Get workers merged tags',
		}),
		getEquipment: route({
			method: 'GET',
			path: '/projects/:projectId/equipment',
			params: projectIdParamsSchema,
			response: z.array(mergedEquipmentSchema),
			operationId: 'getEquipment',
			tags: ['Merged Data'],
			summary: 'Get equipment data',
		}),
		getEquipmentByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/equipment/byLocation',
			params: projectIdParamsSchema,
			query: locationQuerySchema,
			response: z.array(mergedEquipmentSchema),
			operationId: 'getEquipmentByLocation',
			tags: ['Merged Data'],
			summary: 'Get equipment by location',
		}),
		deleteFloors: route({
			method: 'DELETE',
			path: '/projects/:projectId/floors',
			params: projectIdParamsSchema,
			query: deleteFloorsQuerySchema,
			response: z.string(),
			operationId: 'deleteFloors',
			tags: ['Merged Data'],
			summary: 'Delete merged floors',
		}),
		getActiveManagers: route({
			method: 'GET',
			path: '/projects/:projectId/activeManagers',
			params: projectIdParamsSchema,
			response: z.array(activeManagerSchema),
			operationId: 'getActiveManagers',
			tags: ['Merged Data'],
			summary: 'Get active managers',
		}),
		getManagersMergedTagsByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/managersMergedTags/byLocation',
			params: projectIdParamsSchema,
			query: locationQuerySchema,
			// Service returns `IManagerMergedTag[]` — see `getWorkersMergedTags`.
			response: z.array(managerMergedTagSchema),
			operationId: 'getManagersMergedTagsByLocation',
			tags: ['Merged Data'],
			summary: 'Get managers merged tags by location',
		}),
		getActiveProfessions: route({
			method: 'GET',
			path: '/projects/:projectId/activeProfessions',
			params: projectIdParamsSchema,
			response: z.array(professionWithActiveTagsSchema),
			operationId: 'getActiveProfessions',
			tags: ['Merged Data'],
			summary: 'Get active professions',
		}),
		getHoists: route({
			method: 'GET',
			path: '/projects/:projectId/hoists',
			params: projectIdParamsSchema,
			response: z.array(floorViewSchema),
			operationId: 'getHoists',
			tags: ['Merged Data'],
			summary: 'Get hoists data',
		}),
		// `projectMergedData` named the request, not the resource. What it returns is
		// every merged floor of the project with its tags and equipment attached, and
		// the project is now in the path — so the collection gets to be the noun.
		getProjectMergedData: route({
			method: 'GET',
			path: '/projects/:projectId/mergedFloors',
			params: projectIdParamsSchema,
			response: z.array(fullMergedFloorViewDataSchema),
			operationId: 'getProjectMergedData',
			tags: ['Merged Data'],
			summary: 'Get project merged data (all floors)',
		}),
		syncLocationData: route({
			method: 'POST',
			path: '/projects/:projectId/sync',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'syncLocationData',
			tags: ['Merged Data'],
			summary: 'Sync location data',
		}),
		/**
		 * The last GUARD-BLIND route in the repo. Its project used to sit at
		 * `body.mergedTag.projectId`, and `collectRequestProjectIds` reads only
		 * TOP-LEVEL body keys — so the membership guard never validated it, on a
		 * route that pushes data to every subscribed client of whatever project it
		 * was handed. Only the TRUSSTOR_ADMIN role gate stood in the way.
		 *
		 * `updateTagData` also named nothing: it is "this tag's location changed,
		 * tell the streams".
		 */
		updateTagData: route({
			method: 'POST',
			path: '/projects/:projectId/tags/:tagId/locationUpdate',
			params: projectTagParamsSchema,
			body: tagLocationUpdateBodySchema,
			response: z.string(),
			operationId: 'updateTagData',
			tags: ['Merged Data'],
			summary: 'Report that a tag changed location, and push it to the streams',
		}),
		// `/tags/byLocation/count` is project + three resource levels, which the depth
		// rule forbids. Issues solved the identical shape as
		// `/projects/:projectId/issues/locationCounts`; this follows it.
		getTagsCountByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/tags/locationCounts',
			params: projectIdParamsSchema,
			query: resourceCountQuerySchema,
			response: resourceCountByLocationSchema,
			operationId: 'getTagsCountByLocation',
			tags: ['Merged Data'],
			summary: 'Get tags count by location',
		}),
		getEquipmentCountByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/equipment/locationCounts',
			params: projectIdParamsSchema,
			query: equipmentCountQuerySchema,
			response: resourceCountByLocationSchema,
			operationId: 'getEquipmentCountByLocation',
			tags: ['Merged Data'],
			summary: 'Get equipment count by location',
		}),
	},
	{ root: true }
);

// ============================================================
// SSE Route Definitions (registered via implement(), not implementContract())
//
// These do NOT move. Their paths spell `/merged/…` in full because the contract
// above is now `root: true`, which zeroes the prefix `mergedData.routes.ts`
// passes them. Every character here is load-bearing: the same string, minus the
// `/api` the client sends, is what `locationRealtime.service.ts` publishes to.
// ============================================================

export const equipmentStreamRoute = route({
	method: 'GET',
	path: '/merged/equipment/stream',
	query: sseQuerySchema,
	response: z.any().describe('SSE stream of equipment data'),
	operationId: 'getEquipmentStream',
	tags: ['Merged Data'],
	summary: 'Stream equipment data (SSE)',
});

export const floorsStreamRoute = route({
	method: 'GET',
	path: '/merged/floors/stream',
	query: sseQuerySchema,
	response: z.any().describe('SSE stream of floors data'),
	operationId: 'getFloorsStream',
	tags: ['Merged Data'],
	summary: 'Stream floors data (SSE)',
});

export const dashboardDataStreamRoute = route({
	method: 'GET',
	path: '/merged/dashboardData/stream',
	query: sseQuerySchema,
	response: z.any().describe('SSE stream of dashboard data'),
	operationId: 'getDashboardDataStream',
	tags: ['Merged Data'],
	summary: 'Stream dashboard data (SSE)',
});
